import axios from 'axios'
import PathUtil from '../../modules/util/PathUtil'
import JsonUtil from '../util/JsonUtil'

/**
 * セクション種別
 */
export enum SectionType {
  PRODUCT = 'product',
  INDUSTRY = 'industry',
  DOCUMENT = 'document'
}

// ---------------------------------------------------------------
// 定数
const JSON_PATH = PathUtil.makePath('/json/shimadzu_search/section.json')

// ---------------------------------------------------------------
// Class

/**
 * セクション情報
 */
export class SectionInfo {
  public sectionId = 0
  public sectionCode = ''
  public sectionName = ''
  public sectionShortName = ''
  public type = ''
}

/**
 * セクションの情報
 */
export class SectionItem extends SectionInfo {
  public subcategoryCode = ''
  public subcategoryName = ''
  public subcategoryShortName = ''
  public parent = ''

  /**
   * GETパラメータに指定するときのコード情報
   */
  public queryCode() {
    return this.sectionId + '-' + this.subcategoryCode
  }

  /**
   * オブジェクトの作成
   *
   * @param data JSONレコード
   * @returns JSONから作成したオブジェクト
   */
  public static crate(data: Record<string, unknown>): SectionItem {
    const obj: SectionItem = JsonUtil.jsonToObject<SectionItem>(
      SectionItem,
      data
    )
    return obj
  }
}

export type SectionMap = Map<string, SectionItem>

/**
 * Section取得クラス
 */
export class SectionList {
  private _list: SectionItem[] = []
  private _resultMap: SectionMap = new Map()

  /**
   * JSONからセクション情報を取得する
   */
  private async getJson(): Promise<SectionItem[]> {
    // 取得済みの場合、そちらを返却
    if (this._list.length > 0) {
      return this._list
    }

    let list: SectionItem[] = []
    try {
      const res = await axios.get(JSON_PATH)
      const data = res.data
      list = await data.map(SectionItem.crate)
      this._list = list
    } catch (e) {
      // console.error(e)
      throw new Error('Failed to retrieve section list.')
    }

    return list
  }

  /**
   * JSONからセクション情報を取得する
   */
  public async getSectionList(): Promise<SectionItem[]> {
    let list: SectionItem[] = []
    try {
      list = await this.getJson()
    } catch (e) {
      list = []
      // console.error(e)
    }

    return list
  }

  /**
   * セクションのマップ情報を取得する
   */
  public async getSectionMap(): Promise<SectionMap> {
    let list: SectionItem[] = []
    try {
      list = await this.getJson()

      // 取得したリストをマップにセットする
      for (const item of list) {
        const key = SectionList.getSectionKey(
          item.sectionCode,
          item.subcategoryCode
        )
        this._resultMap.set(key, item)
      }
    } catch (e) {
      this._resultMap = new Map()
      // console.error(e)
    }

    return this._resultMap
  }

  /**
   * セクションマップのキーを作成する
   *
   * @param sectionCode
   *  セクションコード
   * @param subcategoryCode
   *  サブカテゴリコード
   * @returns
   *  キー
   */
  public static getSectionKey(
    sectionCode: string,
    subcategoryCode: string
  ): string {
    return sectionCode + '_' + subcategoryCode
  }

  /**
   * ユニークなセクション情報を取得する
   */
  public async getUniqSectionList(): Promise<SectionItem[]> {
    let list: SectionItem[] = []
    try {
      list = await this.getJson()

      // セクションが一意になるようにフィルタ
      list = list.filter(function (v1, i1, a1) {
        return (
          a1.findIndex(function (v2) {
            return (
              v1.sectionCode === v2.sectionCode &&
              v1.sectionName === v2.sectionName
            )
          }) === i1
        )
      })
    } catch (e) {
      list = []
      // console.error(e)
    }

    return list
  }

  // --------------------------------------------------------------------------
  // getter / setter
  public get resultMap(): SectionMap {
    return this._resultMap
  }
}
